import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ReactDefaultProps } from "@/types/react";

import { ModalPortal, ModalPortalProps } from "./ModalPortal";
import { ModalSection } from "./ModalSection";
import { ModalHeading } from "./ModalHeading";
import { ModalActions } from "./ModalActions";
import { ModalAction } from "./ModalAction";

type ModalBaseProps = {
  isOpen?: boolean;
  isVisible?: boolean;
  closable?: boolean;
  closeText?: string;
  renderClosed?: boolean;
  trigger?: Element;
  beforeOpen?: () => void;
  afterClose?: () => void;
  portalless?: boolean;
  zIndex?: number;
};

export type ModalProps = ModalBaseProps &
  Omit<ModalPortalProps, "isClosing" | keyof ModalBaseProps>;

const defaultProps = {
  isOpen: true,
  isVisible: false,
  renderClosed: false,
  closable: true,
  closeText: "Schließen",
  trigger: undefined,
  beforeOpen: undefined,
  afterClose: undefined,
  portalless: false,
  zIndex: 1,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ModalBaseProps,
  typeof defaultProps
> = defaultProps;

export const Modal = (props: ModalProps): JSX.Element => {
  const {
    isOpen: initialIsOpen,
    renderClosed,
    afterClose: afterCloseProp,
    zIndex = defaultProps.zIndex,
    ...otherProps
  } = props;
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (initialIsOpen) {
      setIsClosing(true);
      setIsOpen(false);
    }
  }, [initialIsOpen]);

  const afterClose = () => {
    setIsClosing(false);
    setIsOpen(false);
    afterCloseProp?.();
  };

  if (!renderClosed && !isOpen && !isClosing) return <></>;

  return (
    <ModalPortal
      {...otherProps}
      zIndex={zIndex}
      isOpen={initialIsOpen}
      isClosing={isClosing}
      renderClosed={renderClosed}
      afterClose={afterClose}
    />
  );
};

Modal.defaultProps = validatedDefaultProps;

Modal.propTypes = {
  isOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  renderClosed: PropTypes.bool,
  closable: PropTypes.bool,
  closeText: PropTypes.string,
  trigger:
    typeof Element !== "undefined"
      ? PropTypes.instanceOf(Element)
      : (props: ModalProps, propName: keyof ModalProps) =>
          props[propName] === undefined
            ? null
            : Error(`${propName} must be undefined`),
  beforeOpen: PropTypes.func,
  afterClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  portalless: PropTypes.bool,
  zIndex: PropTypes.number,
};

Modal.displayName = "Modal";

Modal.Section = ModalSection;

Modal.Heading = ModalHeading;

Modal.Actions = ModalActions;

Modal.Action = ModalAction;

export default Modal;
